import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { colors, fonts } from '../../styles/theme'
import { IconType } from '../system/icons'
import FAQ from '../../models/event/faq'
import { bumpRichTextHeading } from '../../utils/rich-text-utils'
import IconButton from '../system/forms/icon-button'
import breakpoints from '../../styles/breakpoints'

const StyledFAQ = styled.section`
  margin-top: ${props => props.theme.spacing.base400};
  @media (min-width: ${breakpoints.md}) {
    margin-top: ${props => props.theme.spacing.base700};
  }
`

const EventList = styled.ul`
  background-color: ${colors.neutral100};
`

const selectedBackgroundColor = colors.primary600
const borderColor = colors.neutral300

const FAQEntry = styled.li`
  ${({ selected }: { selected: boolean }) =>
    !selected &&
    css`
      border: 1px solid ${borderColor};
    `}

  &:not(:first-of-type) {
    border-top: 0;
  }
`

const FAQQuestion = styled.h4`
  display: grid;
  grid-template-columns: 1fr auto;
  font-family: ${fonts.openSans};

  cursor: pointer;
  margin: 0;
  padding: ${props => props.theme.spacing.base400};
  transition: color ${props => props.theme.transitions.links} ease,
    background-color ${props => props.theme.transitions.links} ease;

  ${({ selected }: { selected: boolean }) =>
    selected
      ? css`
          background-color: ${selectedBackgroundColor};
          color: white;
        `
      : css`
          &:hover {
            color: ${props => props.theme.colors.linkHoverColor};
          }
        `}
`

const ExpandIcon = styled(IconButton)`
  justify-self: end;
  align-self: center;
`

const FAQAnswer = styled.div`
  display: none;
  padding: ${props => props.theme.spacing.base400};

  ${({ selected }: { selected: boolean }) =>
    selected &&
    css`
      border: 1px solid ${borderColor};
      border-top: 0;
      display: block;
    `}
`

const EventFAQ = ({ faqs, ...other }: { faqs?: FAQ[] }) => {
  const [selectedQuestion, setSelectedQuestion] = useState(-1)

  const toggleSelectQuestion = (index: number) => {
    if (selectedQuestion === index) {
      setSelectedQuestion(-1)
      return
    }

    setSelectedQuestion(index)
  }

  if (!faqs || faqs.length === 0) {
    return null
  }

  return (
    <StyledFAQ {...other}>
      <h2>Frequently Asked Questions</h2>

      <EventList>
        {faqs.map((faq, index) => {
          const headingBumpedAnswer = bumpRichTextHeading(3, faq.answer)
          const isSelected = selectedQuestion === index

          return (
            <FAQEntry key={index} selected={isSelected}>
              <FAQQuestion
                selected={isSelected}
                aria-expanded={isSelected}
                onClick={() => toggleSelectQuestion(index)}
              >
                {faq.question}
                <ExpandIcon
                  icon={isSelected ? IconType.ExpandUp : IconType.ExpandDown}
                  aria-hidden="true"
                />
              </FAQQuestion>
              <FAQAnswer selected={isSelected} aria-expanded={isSelected}>
                {documentToReactComponents(headingBumpedAnswer)}
              </FAQAnswer>
            </FAQEntry>
          )
        })}
      </EventList>
    </StyledFAQ>
  )
}

export default EventFAQ
